import { defineStore } from "pinia";
import { shareApi } from "@/api";
import type { ShareChannelItem } from "@/api/share";
import { useUserStore } from "./user";
import { GAME_ID } from "./index";
import { useLanguageStore } from "./language";

export interface ShareThreadInfo {
  tid: number;
  thread_title: string;
  thread_url: string;
  /** 帖子摘要，用于分享（如正文前 200 字） */
  description: string;
}

export const useShareStore = defineStore("share", {
  state: () => ({
    /** 频道列表（来自 getChannels 接口） */
    channelList: [] as ShareChannelItem[],
    /** 频道加载中 */
    channelLoading: false,
    /** 频道加载错误 */
    channelError: null as string | null,
    /** 提交分享加载中 */
    submitLoading: false,
  }),

  actions: {
    /**
     * 获取分享频道列表
     */
    async getChannels() {
      this.channelLoading = true;
      this.channelError = null;
      try {
        const res = await shareApi.getChannels();
        if (res.code === 200 && Array.isArray(res.data)) {
          this.channelList = res.data;
          return this.channelList;
        }
        this.channelError = res.message || "获取频道列表失败";
        return [];
      } catch (e: any) {
        this.channelError = e?.message || "获取频道列表失败";
        return [];
      } finally {
        this.channelLoading = false;
      }
    },

    /**
     * 提交分享
     * 分享前会先 checkBind，未绑定则打开绑定弹窗并返回 false
     * @param channelId 频道 id
     * @param threadInfo 帖子信息 tid、thread_title、thread_url、description
     * @returns { success, message }
     */
    async submitShare(
      channelId: string,
      threadInfo: ShareThreadInfo
    ): Promise<{ success: boolean; message?: string; code?: number }> {
      const userStore = useUserStore();
      if (!userStore.checkBind()) {
        return { success: false, message: "请先绑定角色" };
      }
      const bindInfo = userStore.bindInfo;
      if (!bindInfo?.role_id || !bindInfo?.server_id) {
        return { success: false, message: "请先绑定角色" };
      }

      this.submitLoading = true;
      try {
        const res = await shareApi.submitShare({
          game_id: GAME_ID,
          role_id: bindInfo.role_id,
          server_id: bindInfo.server_id,
          channel_id: channelId,
          tid: threadInfo.tid,
          thread_title: threadInfo.thread_title,
          thread_url: threadInfo.thread_url,
          description: threadInfo.description,
          language: useLanguageStore().targetLanguage,
        });
        if (res.code === 200) {
          return { success: true, message: res.message };
        }
        return {
          success: false,
          message: res.message || "分享失败",
          code: res.code,
        };
      } catch (e: any) {
        return {
          success: false,
          message: e?.message || "分享失败",
        };
      } finally {
        this.submitLoading = false;
      }
    },
  },
});
